<template>
    <div>
        <div class="box view-nav-bar">
            <b-form-select v-model="saleshouse"
                           :options="saleshouseOptions"
                           class="transparent-input shx-th-120"
                           @change="onSaleshouseChange"/>

            <b-form-input v-model="from"
                          class="transparent-input"
                          type="text"
                          placeholder="From (e.g. 2026C01)"/>
            <b-form-input v-model="to"
                          class="transparent-input"
                          type="text"
                          placeholder="To (e.g. 2026C52)"/>

            <b-pagination v-if="totalRows !== null"
                          v-model="currentPage"
                          :total-rows="totalRows"
                          :per-page="perPage"
                          aria-controls="adm-week-table"/>
        </div>

        <div class="box">
            <div :class="'box-content pt-3' + (totalRows ? '' : ' d-none')">
                <b-table ref="dataTable"
                         id="adm-week-table"
                         class="shx-table w-100"
                         hover dark fixed
                         sort-icon-left
                         :busy="fetching"
                         :fields="columns"
                         :sort-by.sync="sortBy"
                         :sort-desc.sync="sortDesc"
                         :currentPage="currentPage"
                         :perPage="0"
                         :items="fetchData">

                    <template #cell(admissions)="data">
                        <span>{{ formatInt(data.item.admissions) }}</span>
                    </template>

                    <template #cell(real_weighted)="data">
                        <span>{{ data.item.real_weighted !== null ? data.item.real_weighted + '%' : '—' }}</span>
                        <span v-if="data.item.real_weighted !== null && data.item.real_weighted <= 90"
                              class="badge badge-warning ml-1"
                              :title="'Minimum real: ' + data.item.real_min + '%'">⚠</span>
                    </template>

                    <template #cell(actions)="data">
                        <button @click="data.toggleDetails" class="btn btn-link">
                            <icon :name="'chevron-' + (data.detailsShowing ? 'up' : 'down')"/>
                        </button>
                    </template>

                    <template #row-details="data">
                        <admissions-cinemas-table bucket="week"
                                                  :parent-key="data.item.week_code"
                                                  :saleshouse="saleshouse"/>
                    </template>

                    <template #table-busy>
                        <table-spinner/>
                    </template>
                </b-table>
            </div>

            <div v-if="!totalRows" class="box-content pt-4 text-center text-muted">No admissions yet.</div>
        </div>

        <div class="box">
            <b-pagination v-if="totalRows !== null"
                          v-model="currentPage"
                          :total-rows="totalRows"
                          :per-page="perPage"
                          aria-controls="adm-week-table"/>
        </div>
    </div>
</template>

<script>
import {BFormInput, BFormSelect, BPagination, BTable} from "bootstrap-vue";
import axios from "axios";
import helpers from "../../helpers/helpers";
import TableSpinner from "../../partials/TableSpinner";
import AdmissionsCinemasTable from "../../partials/AdmissionsCinemasTable";
import dataTableLoading from "../../mixins/data-table-loading";

export default {
    components: {
        AdmissionsCinemasTable,
        TableSpinner,
        BFormInput,
        BFormSelect,
        BPagination,
        BTable,
    },
    mixins: [
        dataTableLoading
    ],
    asyncData({store}) {
        return store.dispatch('fetchSlices');
    },
    data() {
        const params = helpers.getCurrentUrlQuery();
        const storeGetters = this.$store.getters;
        const slices = storeGetters.slices || [];
        const current = slices.length ? storeGetters.current : null;
        const defaultTo = current ? current.code : '';
        const defaultFrom = (() => {
            if (!current || !slices.length) return '';
            const currentIdx = slices.findIndex(s => s.code === current.code);
            const backIdx = Math.max(0, currentIdx - 13);
            return slices[backIdx] ? slices[backIdx].code : '';
        })();

        this.columns = [
            {key: 'week_code', label: 'Week', sortable: true, thClass: 'shx-th-100'},
            {key: 'week_start', label: 'Start', sortable: false, thClass: 'shx-th-120'},
            {key: 'week_end', label: 'End', sortable: false, thClass: 'shx-th-120'},
            {key: 'admissions', label: 'Admissions', sortable: true, thClass: 'text-right', tdClass: 'text-right'},
            {key: 'cinemas', label: '# Cinemas', sortable: true, thClass: 'text-right shx-th-100', tdClass: 'text-right'},
            {key: 'titles', label: '# Titles', sortable: true, thClass: 'text-right shx-th-100', tdClass: 'text-right'},
            {key: 'real_weighted', label: 'Real %', sortable: true, thClass: 'text-right shx-th-100', tdClass: 'text-right'},
            {key: 'actions', label: '', thClass: 'shx-th-60', tdClass: 'p-0 text-center align-middle'},
        ];

        return {
            fetching: false,
            saleshouse: params.saleshouse || 'BF',
            saleshouseOptions: [
                {value: 'BF', text: 'BF'},
                {value: 'all', text: 'All'},
            ],
            from: params.from || defaultFrom,
            to: params.to || defaultTo,
            currentPage: +params.page || 1,
            perPage: this.$config.tableRows,
            totalRows: null,
            sortBy: params.sortBy || 'week_code',
            sortDesc: params.sort ? params.sort === 'desc' : true,
        }
    },
    watch: {
        from() {
            this.currentPage = 1;
            this.$refs.dataTable.refresh();
        },
        to() {
            this.currentPage = 1;
            this.$refs.dataTable.refresh();
        },
    },
    methods: {
        onSaleshouseChange() {
            this.currentPage = 1;
            this.$refs.dataTable.refresh();
        },
        fetchData(ctx, callback) {
            this.tableLoading(true);

            axios
                .get('/admissions/week', {
                    params: {
                        saleshouse: this.saleshouse,
                        ...this.from && {from: this.from},
                        ...this.to && {to: this.to},
                        orderBy: ctx.sortBy,
                        order: ctx.sortDesc ? 'desc' : 'asc',
                        page: ctx.currentPage,
                    }
                })
                .then(response => {
                    callback(response.data.data.rows);
                    this.totalRows = response.data.data.total;
                    this.mergeSaleshouseOptions(response.data.data.saleshouses);
                })
                .then(() => {
                    this.tableLoading(false).replaceCurrentUri(ctx);
                })
                .catch(error => {
                    this.tableLoading(false).$toast.error(error.message);
                });
        },
        mergeSaleshouseOptions(list) {
            if (!Array.isArray(list)) return;
            const dynamic = list.map(sh => ({value: sh, text: sh}));
            const seen = new Set();
            this.saleshouseOptions = [
                ...dynamic,
                {value: 'all', text: 'All'},
            ].filter(opt => {
                if (seen.has(opt.value)) return false;
                seen.add(opt.value);
                return true;
            });
        },
        replaceCurrentUri(ctx) {
            this.$router.replace({
                name: 'AdmissionsByWeek',
                query: {
                    saleshouse: this.saleshouse,
                    ...this.from && {from: this.from},
                    ...this.to && {to: this.to},
                    sortBy: ctx.sortBy,
                    sort: ctx.sortDesc ? 'desc' : 'asc',
                    page: ctx.currentPage,
                }
            });
        },
        formatInt(v) {
            return (v || 0).toLocaleString();
        },
    }
}
</script>
